import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import type { IExpandedIconProps } from './types';
import './index.scss';

const DEFAULT_EXPANDED_ICON = 'arrow-up';
const DEFAULT_COLLAPSED_ICON = 'icon-arrow-down';

const resolveTarget = (
  target?: string | React.RefObject<HTMLElement | null>
): HTMLElement | null => {
  if (!target) return null;
  if (typeof target === 'string') return document.querySelector(target);
  return target.current ?? null;
};

const ExpandedIcon: React.FC<IExpandedIconProps> = (props) => {
  const {
    expanded: controlledExpanded,
    defaultExpanded = true,
    onChange,
    expandedIconType = DEFAULT_EXPANDED_ICON,
    collapsedIconType = DEFAULT_COLLAPSED_ICON,
    className,
    style,
    target,
  } = props;

  const isControlled = controlledExpanded !== undefined;
  const [innerExpanded, setInnerExpanded] = useState(defaultExpanded);
  const expanded = isControlled ? (controlledExpanded as boolean) : innerExpanded;

  const latestTargetRef = useRef(target);
  latestTargetRef.current = target;

  const applyTarget = useCallback((nextExpanded: boolean) => {
    // 刻意使用 ref 读取最新 target，并保持 applyTarget 引用稳定：
    // 避免把 target 放进依赖导致回调在每次 target 变化时重建。
    const el = resolveTarget(latestTargetRef.current);
    if (!el) return;

    el.setAttribute('data-collapsed', String(!nextExpanded));
    if (!nextExpanded) {
      el.style.maxHeight = '0px';
    } else {
      el.style.maxHeight = `${el.scrollHeight}px`;
    }
  }, []);

  const setExpanded = useCallback(
    (nextExpanded: boolean) => {
      if (!isControlled) {
        setInnerExpanded(nextExpanded);
      }
      onChange?.(nextExpanded);
      if (target) {
        applyTarget(nextExpanded);
      }
    },
    [applyTarget, isControlled, onChange, target]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpanded(!expanded);
    },
    [expanded, setExpanded]
  );

  const iconType = useMemo(
    () => (expanded ? expandedIconType : collapsedIconType),
    [collapsedIconType, expanded, expandedIconType]
  );

  return (
    <span
      className={classNames('expanded-icon', className)}
      style={style}
      onClick={handleClick}
    >
      <SvgIcon type={iconType} />
    </span>
  );
};

export default memo(ExpandedIcon);

